import { gql, NetworkStatus, useApolloClient, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import cx from 'classnames'
import * as stringify from 'csv-stringify/lib/browser/sync'
import { formatDate } from 'helpers/string'
import { orderBy } from 'lodash'
import Pusher from 'pusher-js'
import { useEffect, useState } from 'react'
import { FaSync } from 'react-icons/fa'
import rollbar from '../../helpers/rollbar'
import { DashboardTitle } from './DashboardTitleContext'
import {
  IdGenerationBatchesQuery,
  IdGenerationBatchesQuery_idGenerationBatches,
} from './__generated__/IdGenerationBatchesQuery'
import {
  IdGenerationBatchKitIdQuery,
  IdGenerationBatchKitIdQueryVariables,
} from './__generated__/IdGenerationBatchKitIdQuery'
import {
  MarkIdGenerationBatchDownloadedMutation,
  MarkIdGenerationBatchDownloadedMutationVariables,
} from './__generated__/MarkIdGenerationBatchDownloadedMutation'

const idGenerationBatchesQuery = gql`
  query IdGenerationBatchesQuery {
    idGenerationBatches {
      id
      batchName
      notes
      sku
      lab
      thirdPartyId
      requestedCount
      createdKitCount
      createdAt
      logEvents {
        event
        time
      }
    }
  }
`

const idGenerationBatchKitIdQuery = gql`
  query IdGenerationBatchKitIdQuery($batchId: ID!) {
    idGenerationBatchById(id: $batchId) {
      id
      kitIds
    }
  }
`

const markIdGenerationBatchDownloadedMutation = gql`
  mutation MarkIdGenerationBatchDownloadedMutation($id: ID!) {
    markIdGenerationBatchDownloaded(id: $id) {
      idGenerationBatch {
        id
        logEvents {
          event
          time
        }
      }
    }
  }
`

const BatchRow = ({ batch }: { batch: IdGenerationBatchesQuery_idGenerationBatches }) => {
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)

  const onDownload = async () => {
    try {
      setLoading(true)
      const response = await client.query<IdGenerationBatchKitIdQuery, IdGenerationBatchKitIdQueryVariables>({
        query: idGenerationBatchKitIdQuery,
        variables: { batchId: batch.id },
        fetchPolicy: 'network-only',
      })

      if (!response.data.idGenerationBatchById) {
        throw new Error('Bad response')
      }

      const csv = stringify(response.data.idGenerationBatchById.kitIds.map((id) => [id]))

      const filename = `IdBatch-${batch.batchName}-${formatDate(new Date().toISOString())}.csv`
      const blob = new Blob([csv], { type: 'application/csv' })
      const a = document.createElement('a')
      const url = URL.createObjectURL(blob)
      a.href = url
      a.download = filename
      a.onclick = () => {
        setTimeout(() => URL.revokeObjectURL(url), 100)
        client.mutate<MarkIdGenerationBatchDownloadedMutation, MarkIdGenerationBatchDownloadedMutationVariables>({
          mutation: markIdGenerationBatchDownloadedMutation,
          variables: { id: batch.id },
        })
      }
      a.click()
    } catch (err) {
      console.error(err)
      rollbar.error(err)
      alert(err.message)
    } finally {
      setLoading(false)
    }
  }

  const isComplete = batch.createdKitCount >= batch.requestedCount
  const lastDownload = orderBy(batch.logEvents, (x) => x.time, 'desc').find((x) => x.event === 'DOWNLOADED')?.time

  return (
    <tr>
      <td>{batch.batchName}</td>
      <td>
        <span className="has-color-primary is-family-monospace">{batch.sku}</span> x {batch.requestedCount}
        <br />
        lab: {batch.lab}
      </td>
      <td>
        {Math.floor((batch.createdKitCount / batch.requestedCount) * 100)}% (Created {batch.createdKitCount})
      </td>
      <td>
        {lastDownload && <p>Last downloaded at: {formatDate(lastDownload)}</p>}

        {isComplete && (
          <button
            className={cx('button', {
              'is-loading': loading,
              'is-primary': !lastDownload,
            })}
            disabled={loading}
            onClick={onDownload}
          >
            {lastDownload ? 'Download again' : 'Download'}
          </button>
        )}
      </td>
    </tr>
  )
}

const BatchList = () => {
  const { loading, error, data, refetch, networkStatus } = useQuery<IdGenerationBatchesQuery>(
    idGenerationBatchesQuery,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  )

  const client = useApolloClient()

  useEffect(() => {
    const pusher = new Pusher(process.env.GATSBY_PUSHER_APP_KEY, {
      cluster: process.env.GATSBY_PUSHER_CLUSTER,
    })

    const channel = pusher.subscribe('dash')
    channel.bind('batch-count-update', function (data) {
      client.cache.modify({
        id: `IdGenerationBatch:${data.batchId}`,
        fields: {
          createdKitCount(existingCreatedKitCount) {
            return data.count > existingCreatedKitCount ? data.count : existingCreatedKitCount
          },
        },
      })
    })

    return () => pusher.unsubscribe('dash')
  }, [client.cache])

  if (error) {
    return <p>ERROR {error.message}</p>
  }

  if (loading && !data) {
    return <p>Loading...</p>
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">{data.idGenerationBatches.length} batches</p>
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus === NetworkStatus.refetch,
              })}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </nav>

      <table className="table is-bordered is-fullwidth" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Contents</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {data.idGenerationBatches.map((batch) => (
            <BatchRow key={batch.id} batch={batch} />
          ))}
        </tbody>
      </table>
    </>
  )
}

const IdGenerationBatches = (props: RouteComponentProps) => {
  return (
    <div className="container">
      <DashboardTitle title="ID Generation Batches" />

      <BatchList />
    </div>
  )
}

export default IdGenerationBatches
